/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps, ReactNode } from 'react'
import { forwardRef } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { Controller, useFormContext, useFormState } from 'react-hook-form'

import { mergeRefs } from '../../../lib/utils'
import { Field, FieldDescription, FieldError } from '../../ui/field'
import { Switch } from '../../ui/switch'

export type SwitchFormFieldProps = {
  checkboxProps?: ComponentProps<typeof Switch>
  children?: ReactNode
  disabled?: boolean
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  label?: string
  name: string
  onCheckedChange?: (checked: boolean) => void
  required?: boolean
  rules?: RegisterOptions
}

export const SwitchFormField = forwardRef<HTMLInputElement, SwitchFormFieldProps>((props, forwardedRef) => {
  const { control } = useFormContext()
  const { errors } = useFormState({ control, name: props.name })

  const error = errors[props.name]
  const invalid = !!error

  return (
    <Field {...props.fieldProps} data-invalid={invalid}>
      <Controller
        control={control}
        name={props.name}
        render={({ field }) => (
          <div className="flex items-center gap-3">
            <Switch
              aria-invalid={invalid}
              checked={!!field.value}
              disabled={props.disabled}
              inputRef={mergeRefs(field.ref, forwardedRef)}
              name={field.name}
              onBlur={field.onBlur}
              onCheckedChange={(checked) => {
                field.onChange(checked)
                props.onCheckedChange?.(checked)
              }}
              required={props.required}
              {...props.checkboxProps}
            />
            {props.children !== undefined || props.label !== undefined ? (
              <span className="text-base leading-snug">{props.children ?? props.label}</span>
            ) : null}
          </div>
        )}
        rules={props.rules}
      />
      {props.helperText !== undefined ? <FieldDescription>{props.helperText}</FieldDescription> : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

SwitchFormField.displayName = 'SwitchFormField'
